import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';

import {
    Typography,
    TooltipAnchor,
    FormComputed,
    TOOLTIP_BOTTOM_LOCATION,
    TOOLTIP_MIDDLE_ALIGNMENT,
    CondensedTheme,
    FormValue,
} from '@ratehub/base-ui';
import { calculateCMHCInsurance as cmhcCalculation, DownPaymentInput } from '@ratehub/mtg-common';

import getDownPaymentPercent from '../functions/getPaymentCalculatorDownPaymentPercent';
import getCalculatorGridColumnClassNames from '../functions/getCalculatorGridColumnClassNames';
import isColumnHidden from '../functions/isColumnHidden';
import COLUMN_NUMBERS from '../definitions/CalculatorColumnNumbers';


const CMHC_CALCULATIONS = COLUMN_NUMBERS.map(
    column => values =>
        calculateCMHCInsurance({
            formValues: values,
            scenario: column,
        }),
);

const TOTAL_MORTGAGE_CALCULATIONS = COLUMN_NUMBERS.map(column => ({ askingPrice, scenarios }) => {
    if (!askingPrice || scenarios[column].cmhcInsurance == null || scenarios[column].downPayment?.dollars == null) {
        return null;
    }

    return askingPrice + scenarios[column].cmhcInsurance - scenarios[column].downPayment?.dollars;
});

const TOOLTIP_WIDTH = '20rem';

function CalculationGridRows({ scenarioCount, ...otherProps }) {
    return (
        <CondensedTheme>

            <div {...otherProps}>
                <div className="row">
                    <div className="col-0">
                        <span className={classNames('operator', 'blue-dark', Typography.CLASSES.TITLE_2XS)}>
                            {'-'}
                        </span>
                        <span className={Typography.CLASSES.TEXT_M}>
                            <FormattedMessage
                                id="mtg-calculator.calulation-grid-rows.downPaymentLabel"
                                defaultMessage="Down payment"
                            />
                        </span>

                        <TooltipAnchor
                            className="tooltip"
                            message={(
                                <FormattedMessage
                                    id="mtg-calculator.calulation-grid-rows.downPaymentTooltip"
                                    defaultMessage="The amount of money you pay up front to obtain a mortgage.
                                        The minimum down payment in Canada is 5%.
                                        For down payments of less than 20%, home buyers are required to purchase mortgage default insurance, commonly referred to as CMHC insurance."
                                />
                            )}
                            tooltipLocation={TOOLTIP_BOTTOM_LOCATION}
                            nubbinAlignment={TOOLTIP_MIDDLE_ALIGNMENT}
                            tooltipWidth={TOOLTIP_WIDTH}
                        />
                    </div>

                    <For
                        each="column"
                        of={COLUMN_NUMBERS}
                    >
                        <div
                            key={`column-${column}`}
                            className={getCalculatorGridColumnClassNames(column, scenarioCount)}
                            aria-hidden={isColumnHidden(column, scenarioCount)}
                        >
                            <FormValue>
                                {({ askingPrice, scenarios }) => (
                                    <DownPaymentInput
                                        name={`scenarios[${column}].downPayment`}
                                        purchasePrice={askingPrice}
                                        value={{
                                            percent: getDownPaymentPercent(column, askingPrice),
                                        }}
                                        onValidate={downPayment => {
                                            const amortization = scenarios[column]?.amortization;
                                            const downPaymentPercent = downPayment?.percent;

                                            if (downPaymentPercent < 20 && amortization > 25) {
                                                return (
                                                    <FormattedMessage
                                                        id="mtg-calculator.calulation-grid-rows.downPayment.validation.amortization"
                                                        defaultMessage="A down payment of 20% or more is required for an amortizations over 25 years."
                                                    />
                                                );
                                            }
                                        }}
                                        // show immediate feedback for invalid down payment
                                        canShowInvalid={!!askingPrice}
                                    />
                                )}
                            </FormValue>
                        </div>
                    </For>
                </div>

                <div className="row">
                    <div className="col-0">
                        <span className={classNames('operator', 'blue-dark', Typography.CLASSES.TITLE_2XS)}>
                            {'+'}
                        </span>

                        <span className={Typography.CLASSES.TEXT_M}>
                            <FormattedMessage
                                id="mtg-calculator.calulation-grid-rows.cmhcInsuranceLabel"
                                defaultMessage="CMHC insurance"
                            />
                        </span>

                        <TooltipAnchor
                            className="tooltip"
                            message={(
                                <FormattedMessage
                                    id="mtg-calculator.calulation-grid-rows.cmhcInsuranceTooltip"
                                    defaultMessage="Mortgage default insurance, commonly referred to as CMHC insurance, protects the lender in the case the borrower defaults on the mortgage. Mortgage default insurance is required on all mortgages with down payments of less than 20%, which are known as high ratio mortgages. Mortgage default insurance is calculated as a percentage applied to your mortgage amount."
                                />
                            )}
                            tooltipLocation={TOOLTIP_BOTTOM_LOCATION}
                            nubbinAlignment={TOOLTIP_MIDDLE_ALIGNMENT}
                            tooltipWidth={TOOLTIP_WIDTH}
                        />
                    </div>

                    <For
                        each="column"
                        of={COLUMN_NUMBERS}
                    >
                        <div
                            key={`column-${column}`}
                            className={getCalculatorGridColumnClassNames(column, scenarioCount)}
                            aria-hidden={isColumnHidden(column, scenarioCount)}
                        >
                            <FormComputed
                                className="blue-dark rh-text-m weight-medium rh-ml-1"
                                data-name={`cmhcInsurance-col-${column}`}
                                name={`scenarios[${column}].cmhcInsurance`}
                                value={CMHC_CALCULATIONS[column]}
                                format="CAD0"
                                hidden={false}
                            />
                        </div>
                    </For>
                </div>

                <div className="row row--blue-light">
                    <div className="col-0">
                        <span className="operator blue-dark rh-text-m weight-medium">
                            {'='}
                        </span>
                        <span className="blue-dark rh-text-m weight-medium">
                            <FormattedMessage
                                id="mtg-calculator.calulation-grid-rows.totalMortgageLabel"
                                defaultMessage="Total mortgage"
                            />
                        </span>
                    </div>

                    <For
                        each="column"
                        of={COLUMN_NUMBERS}
                    >
                        <div
                            key={`column-${column}`}
                            className={getCalculatorGridColumnClassNames(column, scenarioCount)}
                            aria-hidden={isColumnHidden(column, scenarioCount)}
                        >
                            <FormComputed
                                className="blue-dark rh-text-m weight-medium rh-ml-1"
                                data-name={`totalMortgage-col-${column}`}
                                name={`scenarios[${column}].totalMortgage`}
                                value={TOTAL_MORTGAGE_CALCULATIONS[column]}
                                format="CAD0"
                                hidden={false}
                            />
                        </div>
                    </For>
                </div>
            </div>
        </CondensedTheme>
    );
}

CalculationGridRows.propTypes = {
    scenarioCount: PropTypes.number.isRequired,
    values: PropTypes.shape({
        downPayment: PropTypes.shape({
            dollars: PropTypes.number,
            percent: PropTypes.number,
        }),
    }),
};

CalculationGridRows.defaultProps = {
    values: undefined,
};

function calculateCMHCInsurance({ formValues, scenario }) {
    const { askingPrice, scenarios } = formValues;

    if (!askingPrice || scenarios[scenario].downPayment?.dollars == null) {
        return null;
    }

    return cmhcCalculation(askingPrice, scenarios[scenario].downPayment?.dollars);
}

export default observer(CalculationGridRows);
